import { seenPlayers } from '../game/seenPlayers';
import { SK, btnCss, panelCss, titleCss, redHover, rowHover, injectSkinCss } from './skin';

// Sleeping-bag / bed menu (owner: hold-interact a placed bag). A small card with three actions: RENAME the
// bag, PICK IT UP (refunds the item), or GIVE it to another player (they become its owner + spawn point).
// Give reuses the seen-players picker so you never hand-type a username.

export interface BedMenuState { id: number; name: string; kind: 'bag' | 'bed'; }

// A glass card over the live world; Rename = the one primary action, Give secondary, Pick Up = danger outline.
const CARD = [panelCss('glass'), 'min-width:320px', 'max-width:420px',
  'padding:24px', 'box-shadow:0 18px 50px rgba(0,0,0,0.5)', `color:${SK.text}`,
  `font-family:${SK.font}`].join(';');
const TITLE = titleCss(16);
const SUB = `font-size:12px;line-height:1.45;color:${SK.dim};margin-top:8px;`;
const INPUT = `width:100%;margin-top:16px;height:36px;padding:0 12px;background:${SK.well};border:1px solid ${SK.border};border-radius:${SK.rSm};color:${SK.textHi};font:13px ${SK.font};box-sizing:border-box;`;
const BTN = `${btnCss('secondary')};flex:1;`;
const BTN_PRIMARY = `${btnCss('primary')};flex:1;`;
const BTN_DANGER = `${btnCss('danger')};flex:1;`;
const ROW_ITEM = `display:flex;align-items:center;justify-content:space-between;gap:8px;padding:8px 12px;background:${SK.raised};border:1px solid ${SK.border};border-radius:${SK.rSm};cursor:pointer;`;

function el(tag: string, css: string, text?: string): HTMLElement { const e = document.createElement(tag); e.style.cssText = css; if (text != null) e.textContent = text; return e; }

export class BedMenu {
  onRename: ((id: number, name: string) => void) | null = null;
  onPickup: ((id: number) => void) | null = null;
  onGive: ((id: number, name: string) => void) | null = null;
  onClose: (() => void) | null = null; // main relocks the FP pointer here

  private readonly backdrop: HTMLDivElement;
  private readonly card: HTMLDivElement;
  private active = false;
  private state: BedMenuState | null = null;

  constructor() {
    injectSkinCss();
    this.backdrop = document.createElement('div');
    this.backdrop.style.cssText = ['position:fixed', 'inset:0', 'z-index:19', 'display:none', 'align-items:center', 'justify-content:center', `background:${SK.scrim}`].join(';');
    this.card = document.createElement('div');
    this.card.style.cssText = CARD;
    this.backdrop.appendChild(this.card);
    this.backdrop.addEventListener('pointerdown', (e) => { if (e.target === this.backdrop) this.close(); });
    this.backdrop.addEventListener('contextmenu', (e) => { e.preventDefault(); if (e.target === this.backdrop) this.close(); });
    document.body.appendChild(this.backdrop);
    window.addEventListener('keydown', (e) => { if (this.active && e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); this.close(); } }, true);
  }

  get isOpen(): boolean { return this.active; }

  open(state: BedMenuState): void { this.state = state; this.active = true; this.renderMain(); this.backdrop.style.display = 'flex'; }

  close(): void { if (!this.active) return; this.active = false; this.backdrop.style.display = 'none'; this.card.replaceChildren(); this.onClose?.(); }

  private renderMain(): void {
    const s = this.state; if (!s) return;
    this.card.replaceChildren();
    const input = el('input', INPUT) as HTMLInputElement;
    input.type = 'text'; input.value = s.name; input.maxLength = 24; input.placeholder = 'name this bag';
    const renameRow = el('div', 'display:flex;gap:8px;margin-top:12px;');
    const rename = el('button', BTN_PRIMARY, 'Rename');
    rename.addEventListener('click', () => { this.onRename?.(s.id, input.value); this.close(); });
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); this.onRename?.(s.id, input.value); this.close(); } });
    renameRow.append(rename);
    const actions = el('div', 'display:flex;gap:8px;margin-top:16px;');
    const give = el('button', BTN, 'Give to player');
    redHover(give);
    const pickup = el('button', BTN_DANGER, 'Pick Up');
    give.addEventListener('click', () => this.renderGive());
    pickup.addEventListener('click', () => { this.onPickup?.(s.id); this.close(); });
    actions.append(give, pickup);
    this.card.append(
      el('div', TITLE, s.kind === 'bed' ? 'Bed' : 'Sleeping Bag'),
      el('div', SUB, 'Rename it, hand it to a teammate (it becomes their spawn point), or pick it back up.'),
      input, renameRow, actions,
    );
  }

  private renderGive(): void {
    const s = this.state; if (!s) return;
    this.card.replaceChildren();
    const input = el('input', INPUT) as HTMLInputElement;
    input.type = 'text'; input.placeholder = 'search or type a username'; input.maxLength = 24;
    const list = el('div', 'display:flex;flex-direction:column;gap:4px;margin-top:12px;max-height:200px;overflow:auto;');
    const submit = (v: string): void => { const n = v.trim(); if (n) this.onGive?.(s.id, n); this.close(); };
    const render = (): void => {
      list.replaceChildren();
      const names = seenPlayers(input.value);
      if (!names.length) { list.append(el('div', SUB, input.value.trim() ? 'No match — type a name then Give.' : 'No other players seen yet — type a name then Give.')); return; }
      for (const name of names) {
        const r = el('div', ROW_ITEM);
        rowHover(r); // list rows raise neutrally on hover; accent stays reserved for selection
        r.append(el('span', `color:${SK.text};font-size:13px;`, name), el('span', `color:${SK.dim};font-size:12px;`, 'Give ▸'));
        r.addEventListener('click', () => submit(name));
        list.append(r);
      }
    };
    input.addEventListener('input', render);
    input.addEventListener('keydown', (e) => { if (e.key === 'Enter') { e.preventDefault(); submit(input.value); } });
    const back = el('button', BTN, 'Back'); back.style.marginTop = '16px';
    back.addEventListener('click', () => this.renderMain());
    redHover(back);
    this.card.append(el('div', TITLE, 'Give this bag to…'), el('div', SUB, 'They become the owner — it turns into their respawn point.'), input, list, back);
    render(); input.focus();
  }
}
